import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { parseTimeStamp } from '@/lib/parseTimeStamp';
import { EvidenceSubscriptionTier } from '@heidi/schema/ml-scribe';
import { differenceInCalendarDays } from 'date-fns';
import { useMemo } from 'react';

/**
 * Hook to get evidence billing info and check if user has Evidence Pro access
 * User has Evidence Pro if they have evidence_billing and:
 * - Have an active subscription (stripe_subscription_id exists and not canceled)
 * - OR are in a reverse trial
 *
 * Trial/history facts (freeTrialAvailable, hasTrialExpired, isTrialCancelledByUser)
 * come from the backend-owned `evidence_billing_state` contract rather than being
 * derived locally off the raw projection and trial history.
 */
export function useEvidenceBillingInfo() {
  const { data, isPending: isBillingInfoPending } = useQueryUserBillingInfo(
    (billingInfo) => ({
      evidenceBilling: billingInfo?.evidence_billing,
      evidenceBillingState: billingInfo?.evidence_billing_state,
    })
  );
  const evidenceBilling = data?.evidenceBilling;
  const state = data?.evidenceBillingState;

  const isInFreeTrial = useMemo(() => {
    return (
      Boolean(evidenceBilling?.is_in_free_trial) &&
      evidenceBilling?.tier !== EvidenceSubscriptionTier.FREE
    );
  }, [evidenceBilling]);

  const trialDaysRemaining = useMemo(() => {
    const trialEndDate = parseTimeStamp(evidenceBilling?.trial_end_date);
    return differenceInCalendarDays(trialEndDate, new Date());
  }, [evidenceBilling]);

  const hasEvidencePro = useMemo(() => {
    // practice tier can also access pro features,
    // need to update when we differentiate between pro and practice tiers
    return Boolean(
      evidenceBilling &&
      (evidenceBilling?.tier === EvidenceSubscriptionTier.PRO ||
        evidenceBilling?.tier === EvidenceSubscriptionTier.PRACTICE)
    );
  }, [evidenceBilling]);

  const hasEvidencePractice = useMemo(
    () => evidenceBilling?.tier === EvidenceSubscriptionTier.PRACTICE,
    [evidenceBilling?.tier]
  );

  const freeTrialAvailable = Boolean(state?.can_start_evidence_trial);

  // Backend materializes the full predicate (had a personal trial, no Pro, not
  // in trial, user-sourced) and stays correct when `evidence_billing` is null,
  // so the FE no longer derives it off the projection's source/tier.
  const hasTrialExpired = Boolean(state?.evidence_trial_expired);

  const isTrialCancelledByUser = Boolean(state?.evidence_trial_ended_by_user);

  return {
    evidenceBilling,
    isInFreeTrial,
    hasEvidencePro,
    hasEvidencePractice,
    trialDaysRemaining,
    freeTrialAvailable,
    hasTrialExpired,
    isBillingInfoPending,
    isTrialCancelledByUser,
  };
}
